import {
  Box,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { PLAN_INTERVAL_STRINGS } from "@/constants/Plans";
import { useOrgSubscription } from "@/hooks/useSubscriptions";
import type { Organization } from "@/interfaces/organization";
import { subscriptionDisplayStatus, subscriptionStatusColor } from "@/lib/subscriptionStatus";
import { formatDate } from "@/utils/date-format";
import { formatPlanPrice } from "@/utils/plan-price-helper";

/**
 * One organization's subscription, for GFG staff.
 *
 * Distinct from the Plan tab, which is the CLIENT's self-service surface — what plan they are on
 * and the controls to change it. This is the billing record behind it: which cycles have been
 * billed, what was due, what was paid, and the Stripe invoice for each.
 *
 * Reads the projection and its renewal rows, never Stripe. A screen that called Stripe on render
 * would be slow, would fail when Stripe is, and would show something different from what
 * enforcement reads — and if those two disagree that is drift, which `subs:reconcile` surfaces
 * daily rather than a page hiding by papering over it.
 */

function money(amount: string | null, currency: string): string {
  if (amount === null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(Number(amount));
}

/** Stripe's invoice statuses, as they read to a human. */
const RENEWAL_STATUS: Record<
  string,
  { label: string; color: "default" | "success" | "warning" | "error" }
> = {
  paid: { label: "Paid", color: "success" },
  open: { label: "Open", color: "warning" },
  draft: { label: "Draft", color: "default" },
  uncollectible: { label: "Uncollectible", color: "error" },
  void: { label: "Void", color: "default" },
};

export default function OrgSubscriptionPanel({ org }: { org: Organization }) {
  const { data, isLoading } = useOrgSubscription(org.id);
  const sub = data?.subscription ?? null;

  if (isLoading) {
    return <Typography sx={{ color: "var(--color-graphite)" }}>Loading subscription…</Typography>;
  }

  // A real state rather than an error: an organization that predates the backfill, or one created
  // since. Saying so beats an empty table that reads like a bug.
  if (!sub) {
    return (
      <Typography sx={{ color: "var(--color-graphite)" }}>
        This organization has no subscription record yet.
      </Typography>
    );
  }

  const status = subscriptionDisplayStatus(sub);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "flex-start" }}>
        <Field
          // The price on the org's own plan row — "$1,140/year" for a subscriber still on the
          // retired annual price — not what the plan is sold at today.
          label="Plan"
          value={
            sub.billing
              ? `${sub.planName} · ${formatPlanPrice(sub.billing)}${PLAN_INTERVAL_STRINGS.unit[sub.billing.interval]}`
              : sub.planName
          }
        />
        <Field
          label="Status"
          value={
            <Chip
              size="small"
              variant="outlined"
              label={status}
              color={subscriptionStatusColor(status)}
            />
          }
        />
        <Field
          // "Renews" or "Ends" is the cancellation flag deciding a label, not a second date column:
          // the date is the same instant either way, and what changes is what happens at it.
          label={sub.cancelAtPeriodEnd ? "Ends" : "Renews"}
          value={sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : "—"}
        />
        <Field
          // Only meaningful when Stripe is actually collecting. A backfilled row stores
          // `charge_automatically` as a default, and rendering that as "Card" would claim a card is
          // on file for an organization nobody has ever charged.
          label="Billing"
          value={
            !sub.stripeSubscriptionId
              ? "—"
              : sub.collectionMethod === "send_invoice"
                ? "Invoice (net terms)"
                : "Card"
          }
        />
        <Field label="Started" value={formatDate(sub.startedAt)} />
      </Box>

      <Box>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Billing cycles</Typography>
        {sub.renewals.length === 0 ? (
          <Typography sx={{ color: "var(--color-graphite)", fontSize: 14 }}>
            No cycles billed yet — the first is raised when the term begins.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Due</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Recognised</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell>Due date</TableCell>
                <TableCell>Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sub.renewals.map((r) => {
                const s = RENEWAL_STATUS[r.status] ?? {
                  label: r.status,
                  color: "default" as const,
                };
                return (
                  <TableRow key={r.id}>
                    {/* sequence 1 is the INITIAL term, not a renewal. */}
                    <TableCell>{r.sequence}</TableCell>
                    <TableCell>
                      {formatDate(r.cycleStart)} – {formatDate(r.cycleEnd)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={s.label} color={s.color} />
                    </TableCell>
                    <TableCell align="right">{money(r.amountDue, r.currency)}</TableCell>
                    <TableCell align="right">{money(r.amountPaid, r.currency)}</TableCell>
                    {/* From the cycle's revenue schedule: earned so far, and still owed in service. */}
                    <TableCell align="right">{money(r.recognised, r.currency)}</TableCell>
                    <TableCell align="right">{money(r.remaining, r.currency)}</TableCell>
                    <TableCell>{r.dueDate ? formatDate(r.dueDate) : "—"}</TableCell>
                    <TableCell>
                      {r.hostedInvoiceUrl ? (
                        <Link href={r.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                          {r.stripeInvoiceNumber ?? "View"}
                        </Link>
                      ) : (
                        (r.stripeInvoiceNumber ?? "—")
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--color-graphite)",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.5, fontSize: 15 }}>{value}</Box>
    </Box>
  );
}
